// src/Components/Home/FullscreenPage.js

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { SoundWaveIcon } from '../../assets/icons/CustomIcons';
import { useNavigate, useLocation } from 'react-router-dom';

const MediaElement = ({ slide }) => {
  if (!slide.url) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Typography color='text.secondary'>No Media Available</Typography>
      </Box>
    );
  }

  // Strip URL query parameters (like Firebase tokens) to accurately check the extension
  const urlLower = slide.url.toLowerCase();
  const cleanUrl = urlLower.split('?')[0];

  const isVideo =
    slide.type === 'video' || cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm');
  const isAudio =
    slide.type === 'audio' || cleanUrl.endsWith('.mp3') || cleanUrl.endsWith('.wav');

  if (isVideo) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <video
          src={slide.url}
          playsInline
          controls
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    );
  }

  if (isAudio) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e0e0e0',
          gap: 3,
        }}>
        <SoundWaveIcon sx={{ fontSize: 250 }} />
        <audio controls src={slide.url} style={{ width: '80%' }} />
      </Box>
    );
  }

  return (
    <img
      src={slide.url}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      alt={slide.markerName || 'Media'}
    />
  );
};

export default function FullscreenPage({ logoUrl, onSyncMarker }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Safely extract the payload passed via navigate()
  const state = location.state || {};
  const {
    activeSlides = [],
    isAutoMode = false,
    currentIndex: initialIndex = 0,
    isPaused: initialPaused = false,
  } = state;

  // Local state for playback controls
  const [isPaused, setIsPaused] = useState(initialPaused);
  const [autoIndex, setAutoIndex] = useState(initialIndex);

  // Fallback trap: if a user manually forces a reload on /fullscreen, state is wiped.
  useEffect(() => {
    if (!location.state) {
      navigate('/', { replace: true });
    }
  }, [location.state, navigate]);

  // Handle the streaming nature of Auto Mode
  useEffect(() => {
    // If not in auto mode, paused, or there are not enough slides to rotate, do nothing.
    if (!isAutoMode || isPaused || activeSlides.length <= 1) return;

    const timer = setInterval(() => {
      setAutoIndex((prev) => (prev + 1) % activeSlides.length);
    }, 5000); // Swaps content every 5 seconds, similar to Home slick slider

    return () => clearInterval(timer);
  }, [isAutoMode, isPaused, activeSlides.length]);

  const handleCloseWindow = () => {
    // Synchronize the currently viewed marker seamlessly into the dashboard
    if (isAutoMode && activeSlides.length > 0) {
      if (isPaused) {
        const currentSlide = activeSlides[autoIndex];
        if (currentSlide && currentSlide.markerData && onSyncMarker) {
          onSyncMarker(currentSlide.markerData);
        }
      }
      // Return to Home, passing state to update the slider and keep the circles completely intact
      navigate('/', {
        state: { syncedIndex: autoIndex, syncedPaused: isPaused },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  const handlePrev = () => {
    setAutoIndex((prev) => (prev === 0 ? activeSlides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setAutoIndex((prev) => (prev + 1) % activeSlides.length);
  };

  const handleTogglePause = () => {
    setIsPaused((prev) => !prev);
  };

  if (!location.state) return null;

  // In Auto mode: Expand the single active showcase item into its full array of photos
  // In Manual mode: Just map out all active photos vertically
  let displaySlides = [];
  if (isAutoMode && activeSlides.length > 0) {
    const currentItem = activeSlides[autoIndex];
    if (currentItem && currentItem.allPhotos && currentItem.allPhotos.length > 0) {
      displaySlides = currentItem.allPhotos.map((p, i) => ({
        id: p.id || `${currentItem.id}-${i}`,
        url: p.url || p.img,
        // Fallback to marker description only for the very first photo to avoid annoying repetition
        description: p.description || (i === 0 ? currentItem.markerDescription : ''),
        type: p.type || 'image',
        markerName: currentItem.markerName,
        originalIndex: i,
      }));
    } else if (currentItem) {
      displaySlides = [{ ...currentItem, originalIndex: 0 }];
    }
  } else {
    displaySlides = activeSlides.map((slide, i) => ({ ...slide, originalIndex: i }));
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}>
      {/* Fixed Top Bar */}
      <Box
        sx={{
          width: '100%',
          height: '50px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          px: 3,
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 10,
        }}>
        {/* Left Section to balance flex layout */}
        <Box sx={{ flex: 1, flexBasis: 0 }} />

        {/* Center Logo */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            px: 2,
          }}>
          {logoUrl && (
            <Box
              component='img'
              src={logoUrl}
              alt='Client Logo'
              sx={{
                maxHeight: '38px',
                maxWidth: '200px',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>

        {/* Right Section containing Dots and Controls */}
        <Box
          sx={{
            flex: 1,
            flexBasis: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}>
          {/* Centralized Dots for Auto Mode */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              px: 2,
              minWidth: 0,
            }}>
            {isAutoMode && activeSlides.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}>
                {activeSlides.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => {
                      setAutoIndex(i);
                      setIsPaused(true); // Optional: Auto-pause when user manually scrubs via dots
                    }}
                    sx={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      border: '1px solid #000',
                      backgroundColor: i === autoIndex ? '#000' : '#fff',
                      opacity: i === autoIndex ? 1 : 0.5,
                      cursor: 'pointer',
                      transform: i === autoIndex ? 'scale(1.2)' : 'none',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.2)',
                      },
                    }}
                    title={`Go to slide ${i + 1}`}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Right Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {isAutoMode && (
              <>
                <IconButton onClick={handlePrev} sx={{ color: '#333' }} title='Previous'>
                  <NavigateBeforeIcon />
                </IconButton>

                <IconButton
                  onClick={handleTogglePause}
                  sx={{ color: '#333' }}
                  title={isPaused ? 'Play' : 'Pause'}>
                  {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>

                <IconButton onClick={handleNext} sx={{ color: '#333' }} title='Next'>
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}

            {/* Mode Status Badge */}
            <Typography
              variant='caption'
              sx={{
                backgroundColor: !isAutoMode
                  ? '#FF9900'
                  : isPaused
                    ? '#f44336'
                    : '#4caf50',
                color: '#fff',
                px: 1.5,
                py: 0.5,
                mx: 1,
                borderRadius: '4px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                lineHeight: 1.5,
                boxShadow: '0px 2px 5px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '85px',
              }}>
              {!isAutoMode ? 'Manual Mode' : isPaused ? 'Paused' : 'Auto Mode'}
            </Typography>

            {/* Visual Divider */}
            <Box
              sx={{ width: '1px', height: '24px', backgroundColor: '#e0e0e0', mx: 1 }}
            />

            <IconButton
              onClick={handleCloseWindow}
              sx={{
                color: '#333',
                backgroundColor: 'rgba(0,0,0,0.05)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
              }}
              title='Close Fullscreen'>
              <CloseFullscreenIcon sx={{ fontSize: '28px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Scrolling Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 12,
        }}>
        {displaySlides.map((slide) => {
          // Alternating logic based on the strict index
          const isPhotoLeft = slide.originalIndex % 2 === 0;
          const hasRichText =
            slide.description &&
            slide.description.trim() !== '' &&
            slide.description !== '<p></p>';

          return (
            <Box
              key={`${slide.id}-${slide.originalIndex}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start', // Ensures image stays square even if text column grows much taller
                width: '100%',
                mb: 8,
              }}>
              {isPhotoLeft ? (
                <>
                  <Box sx={{ width: '10%', flexShrink: 0 }} />

                  <Box
                    sx={{
                      width: '45%',
                      flexShrink: 0,
                      aspectRatio: '1 / 1',
                      backgroundColor: '#f9f9f9',
                      overflow: 'hidden',
                      borderRadius: '4px',
                    }}>
                    <MediaElement slide={slide} />
                  </Box>

                  <Box sx={{ width: '5%', flexShrink: 0 }} />

                  <Box sx={{ width: '30%', flexShrink: 0, mt: '60px' }}>
                    {hasRichText && (
                      <Box
                        sx={{
                          typography: 'body1',
                          color: 'text.secondary',
                          lineHeight: 1.2,
                          whiteSpace: 'pre-wrap', // Preserve natural line breaks
                          wordBreak: 'break-word',
                          textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
                          '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
                          '& p:last-of-type': { mb: 0 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          },
                          '& ul, & ol': { paddingLeft: '1.5rem', mt: 0, mb: 0 },
                          '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
                          '& li > p': { margin: '0 !important' },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                        }}
                        dangerouslySetInnerHTML={{ __html: slide.description }}
                      />
                    )}
                  </Box>

                  <Box sx={{ width: '10%', flexShrink: 0 }} />
                </>
              ) : (
                <>
                  <Box sx={{ width: '10%', flexShrink: 0 }} />

                  <Box sx={{ width: '30%', flexShrink: 0, mt: '60px' }}>
                    {hasRichText && (
                      <Box
                        sx={{
                          typography: 'body1',
                          color: 'text.secondary',
                          lineHeight: 1.2,
                          whiteSpace: 'pre-wrap', // Preserve natural line breaks
                          wordBreak: 'break-word',
                          textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
                          '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
                          '& p:last-of-type': { mb: 0 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          },
                          '& ul, & ol': { paddingLeft: '1.5rem', mt: 0, mb: 0 },
                          '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
                          '& li > p': { margin: '0 !important' },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                        }}
                        dangerouslySetInnerHTML={{ __html: slide.description }}
                      />
                    )}
                  </Box>

                  <Box sx={{ width: '5%', flexShrink: 0 }} />

                  <Box
                    sx={{
                      width: '45%',
                      flexShrink: 0,
                      aspectRatio: '1 / 1',
                      backgroundColor: '#f9f9f9',
                      overflow: 'hidden',
                      borderRadius: '4px',
                    }}>
                    <MediaElement slide={slide} />
                  </Box>

                  <Box sx={{ width: '10%', flexShrink: 0 }} />
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
